import React, { useState, useEffect, useCallback } from "react";
import { ArrowRight, ArrowLeft, X } from "lucide-react";
import { showSuccess, showError } from "@/shared/utils/toast";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import ProfileSection from "./ProfileSection";
import CompanySection from "./CompanySection";
import { downloadProfile } from "./downloadProfile.js";
import Loader from "@/shared/components/ui/Loaders/Loader";

const ProfileModal = ({
  isOpen,
  onClose,
  selectedId,
  isLoading,
  onLoadingChange,
  fetchProfileData,
  allIds = [],
  profileData: initialProfileData = null,
  contextData = {},
  customComponents = null,
  goToPreviousProfile,
  goToNextProfile,
  skipFetch = false,
}) => {
  const [currentProfile, setCurrentProfile] = useState(initialProfileData);
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [accordion2Open, setAccordion2Open] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const currentIndex = allIds.indexOf(selectedId);
  const totalCount = allIds.length;

  const loadProfileData = useCallback(
    async (id) => {
      if (!fetchProfileData) return;
      if (onLoadingChange) onLoadingChange(true);
      try {
        const data = await fetchProfileData(id);
        setCurrentProfile(data);
      } catch (error) {
        console.error("Error loading profile:", error);
      } finally {
        if (onLoadingChange) onLoadingChange(false);
      }
    },
    [fetchProfileData, onLoadingChange],
  );

  useEffect(() => {
    if (!isOpen || !selectedId) return;
    if (skipFetch) return;

    loadProfileData(selectedId);
  }, [selectedId, isOpen, skipFetch, loadProfileData]);

  useEffect(() => {
    if (skipFetch) {
      setCurrentProfile(initialProfileData);
    } else if (
      initialProfileData &&
      (initialProfileData.profile || initialProfileData.contacts_info)
    ) {
      setCurrentProfile(initialProfileData);
    }
  }, [initialProfileData, skipFetch]);

  const toggleAccordion = () => setAccordionOpen((prev) => !prev);
  const toggleAccordion2 = () => setAccordion2Open((prev) => !prev);

  const selectedProspect = contextData?.selectedProspect ?? null;
  const apiData = currentProfile;
  const contactsInfo = apiData?.contacts_info ?? apiData;
  const companyInfo = apiData?.contact_company_info ?? {};

  const mergedProfileData = React.useMemo(() => {
    if (!apiData) return null;
    const prospect = selectedProspect;
    return {
      name: prospect?.name ?? prospect?.full_name ?? apiData?.name,
      position: prospect?.designation ?? prospect?.position,
      company:
        prospect?.company ?? companyInfo?.company_name ?? apiData?.company,
      metrics: [
        {
          value: contactsInfo?.buying_authority?.authority,
          reason: contactsInfo?.buying_authority?.reason,
        },
        {
          value: contactsInfo?.buying_intent?.intent,
          reason: contactsInfo?.buying_intent?.reason,
        },
        {
          value: contactsInfo?.sales_score?.score,
          reason: contactsInfo?.sales_score?.reason,
        },
      ],
      recommendations:
        contactsInfo?.relevance_and_recommendations_for_prospect ??
        apiData?.recommendations,
      buyingBehaviour:
        contactsInfo?.buying_behaviour_and_interest ?? apiData?.buyingBehaviour,
      buyingIntent:
        contactsInfo?.intent_activity_with_your_company ??
        (contactsInfo?.buying_intent
          ? `${contactsInfo.buying_intent.intent}: ${contactsInfo.buying_intent.reason}`
          : apiData?.buyingIntent),
      actions: apiData?.actions ?? [],
      work_role: contactsInfo?.work_role ?? apiData?.work_role,
    };
  }, [apiData, selectedProspect, contactsInfo, companyInfo]);

  const displayName =
    selectedProspect?.name ?? selectedProspect?.full_name ?? apiData?.name;
  const displayCompany =
    selectedProspect?.company ??
    companyInfo?.company_name ??
    apiData?.company ??
    "Unknown";

  const buildCompanyProfileData = (companyInfo, fallbackName, apiData) => {
    const info = companyInfo ?? {};
    return {
      name: info?.company_name ?? fallbackName,
      company_size: info?.company_size,
      revenue: info?.revenue,
      metrics: [
        {
          value: info?.buying_behaviour?.behaviour,
          reason: info?.buying_behaviour?.reason,
        },
        {
          value: info?.financial_health?.health,
          reason: info?.financial_health?.reason,
        },
        {
          value: info?.match_score?.score,
          reason: info?.match_score?.reason,
        },
      ],
      recommendations: info?.relevance_and_recommendations_for_company,
      financial_trends: info?.financial_trends_on_revenue_and_ebidta,
      products_and_services: info?.products_and_services_breakdown,
      leadership_tree: info?.leadership_tree ?? [],
      buyingBehaviour: info?.buying_behaviour
        ? `${info.buying_behaviour.behaviour}: ${info.buying_behaviour.reason}`
        : undefined,
      buyingIntent: info?.relevance_and_recommendations_for_company,
      key_pains_priority_strategy: info?.key_pains_priority_strategy,
    };
  };

  const handleDownload = async () => {
    if (!mergedProfileData && !apiData) return;
    if (isDownloading) return;

    const downloadData = {
      name: mergedProfileData?.name ?? displayName,
      position: mergedProfileData?.position,
      company: displayCompany,
      contacts_info: contactsInfo,
      contact_company_info: companyInfo,
    };

    setIsDownloading(true);
    try {
      await downloadProfile(downloadData);
      showSuccess("Profile downloaded successfully");
    } catch (err) {
      console.error("Profile download failed:", err);
      showError("Failed to download profile. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[999999999] modal-background bg-black bg-opacity-50 flex justify-center items-center py-10 text-xs"
    >
      <div className="bg-white rounded-lg w-full p-5 max-w-[967px] mx-4 md:mx-auto shadow-lg overflow-hidden transition-all duration-300 ease-in-out">
        {/* Header */}
        <div className="bg-white sticky top-0 z-10 mb-6 flex justify-between items-center">
          <h1 className="text-sm font-medium text-gray-900">
            {displayName} - {displayCompany}
          </h1>
          <div className="flex items-center gap-2">
            <SecondaryButton
              title={isDownloading ? "Generating PDF…" : "Download Profile"}
              onClick={handleDownload}
              textColor="light"
              disabled={!currentProfile || isDownloading}
            />
            <button
              className="p-1 hover:bg-gray-100 rounded-full"
              onClick={onClose}
            >
              <X size={21} color="#181D27" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        {isLoading ? (
          <div className="flex justify-center items-center p-6 min-h-[200px]">
            <Loader text="Fetching profile..." />
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[calc(100vh-180px)] scrollbar-hide">
            {customComponents
              ? // Render custom components
                customComponents
              : mergedProfileData && (
                  <>
                    <ProfileSection
                      selectedProspect={selectedProspect}
                      response={contextData?.response ?? []}
                      accordionOpen={accordionOpen}
                      toggleAccordion={toggleAccordion}
                      profileData={mergedProfileData}
                    />
                    <CompanySection
                      selectedProspect={selectedProspect}
                      response={contextData?.response ?? []}
                      accordionOpen={accordion2Open}
                      toggleAccordion={toggleAccordion2}
                      profileData={buildCompanyProfileData(
                        companyInfo,
                        displayCompany,
                        apiData,
                      )}
                    />
                  </>
                )}
          </div>
        )}

        {/* Navigation Buttons */}
        {allIds?.length > 0 && (
          <div className="flex justify-between mt-4 text-xs font-semibold items-center">
            <div className="text-gray-600 dark:text-gray-400">
              {currentIndex + 1} of {totalCount}
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={goToPreviousProfile}
                disabled={isLoading || currentIndex === 0}
                className="flex border py-2 px-3 gap-2 text-gray-800 dark:text-gray-100 items-center border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-l hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeft size={18} />
                Previous
              </button>
              <button
                type="button"
                onClick={goToNextProfile}
                disabled={isLoading || currentIndex === totalCount - 1}
                className="flex border py-2 px-3 text-gray-800 dark:text-gray-100 gap-2 items-center border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-r hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileModal;
